import React, { useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { Modal } from "semantic-ui-react";
import { OngoingCourse } from "../../../stores/EnrolmentStore/Types";
import { errorNotification, notification } from "../../../utils/helpers";
import { getRequest, postRequest } from "../../../utils/httpRequest";
import {
  getRandomPersonalizationMessage,
  isGeneratingPersonalization,
  nextPersonalizationProgress,
  personalizationMessages,
} from "./helpers";
import { PersonalizationModalContent } from "./PersonalizationModalContent";
import { PersonalizationGeneratingContent } from "./PersonalizationGeneratingContent";

interface Props {
  isModalOpen: boolean;
  closeModal: () => void;
  enrolment: OngoingCourse;
  onStateChange: (personalizationState: string) => void;
  personalizationState: string | null;
}

interface Errors {
  personalizationName?: boolean;
  personalizationCountry?: boolean;
  personalizationWorkingOn?: boolean;
  personalizationMustImprove?: boolean;
}

export const RepersonalizationModal = ({
  isModalOpen,
  closeModal,
  enrolment,
  onStateChange,
  personalizationState: personalizationStateProp,
}: Props) => {
  const pollTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const messageIntervalId = useRef<NodeJS.Timeout | null>(null);
  const pollingStartedAt = useRef<number | null>(null);
  const maxPollTimeInSeconds = 30;
  const [loading, setLoading] = useState(false);
  const [isEnableBusy, setIsEnableBusy] = useState(false);
  const [personalizationState, setPersonalizationState] = useState(
    personalizationStateProp || enrolment.personalization_state,
  );
  const [personalizationName, setPersonalizationName] = useState("");
  const [personalizationCountry, setPersonalizationCountry] = useState("");
  const [personalizationWorkingOn, setPersonalizationWorkingOn] = useState("");
  const [personalizationMustImprove, setPersonalizationMustImprove] =
    useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [personalizationProgress, setPersonalizationProgress] = useState(0);
  const [randomMessage, setRandomMessage] = useState(
    personalizationMessages[0],
  );

  const clearTimers = () => {
    if (pollTimeoutId.current) {
      clearTimeout(pollTimeoutId.current);
      pollTimeoutId.current = null;
    }

    if (messageIntervalId.current) {
      clearInterval(messageIntervalId.current);
      messageIntervalId.current = null;
    }
  };

  const resetPollingStartedAt = () => {
    pollingStartedAt.current = null;
  };

  const stopPolling = () => {
    clearTimers();
    resetPollingStartedAt();
    setPersonalizationState("generating_progress");
    onStateChange("generating_progress");
    closeModal();
    notification(
      "Personalisation in progress. You will be notified once complete.",
      "notification",
    );
  };

  const updatePersonalizationStateFromAPI = (data: any) => {
    setPersonalizationState(data.personalization_state);
    setPersonalizationName(data.personalization_name || "");
    setPersonalizationCountry(data.personalization_country || "");
    setPersonalizationWorkingOn(data.personalization_working_on || "");
    setPersonalizationMustImprove(data.personalization_must_improve || "");
    onStateChange(data.personalization_state);
  };

  const increasePersonalizationProgress = () => {
    setPersonalizationProgress((currentProgress) =>
      nextPersonalizationProgress(currentProgress),
    );
  };

  const getPersonalizationConfig = () => {
    setLoading(true);

    return getRequest(`/api/enrolments/${enrolment.id}/personalization`)
      .then((response: AxiosResponse) => {
        updatePersonalizationStateFromAPI(response.data);
        return response.data;
      })
      .catch((error) => {
        errorNotification(
          "Error when fetching personalisation",
          JSON.stringify(error?.response?.data),
        );

        return null;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const startMessageRotation = () => {
    if (messageIntervalId.current) return;

    messageIntervalId.current = setInterval(() => {
      setRandomMessage(getRandomPersonalizationMessage());
    }, 2500);
  };

  const startPolling = () => {
    clearTimers();
    startMessageRotation();
    pollingStartedAt.current = Date.now();

    const poll = () => {
      increasePersonalizationProgress();

      getPersonalizationConfig().then((data) => {
        const timedOut =
          pollingStartedAt.current &&
          Date.now() - pollingStartedAt.current >= maxPollTimeInSeconds * 1000;

        if (!data) {
          if (timedOut) {
            stopPolling();
            return;
          }

          pollTimeoutId.current = setTimeout(poll, 3000);
          return;
        }

        const nextState = data.personalization_state;

        if (nextState !== "generating") {
          clearTimers();
          pollingStartedAt.current = null;

          if (nextState === "generating_progress") {
            closeModal();
          }
          return;
        }

        if (timedOut) {
          stopPolling();
          return;
        }

        pollTimeoutId.current = setTimeout(poll, 3000);
      });
    };

    pollTimeoutId.current = setTimeout(poll, 3000);
  };

  const activatePersonalization = () => {
    const nextErrors = {
      personalizationName: !personalizationName.trim(),
      personalizationCountry: !personalizationCountry.trim(),
      personalizationWorkingOn: !personalizationWorkingOn.trim(),
      personalizationMustImprove: !personalizationMustImprove.trim(),
    };

    const hasErrors =
      nextErrors.personalizationName ||
      nextErrors.personalizationCountry ||
      nextErrors.personalizationWorkingOn ||
      nextErrors.personalizationMustImprove;

    if (hasErrors) {
      setErrors(nextErrors);
      return;
    }

    setIsEnableBusy(true);
    setErrors({});
    setPersonalizationState("generating");
    setPersonalizationProgress(10);
    setRandomMessage(personalizationMessages[0]);
    onStateChange("generating");

    postRequest(`/api/enrolments/${enrolment.id}/personalization/activate`, {
      personalization_name: personalizationName,
      personalization_country: personalizationCountry,
      personalization_working_on: personalizationWorkingOn,
      personalization_must_improve: personalizationMustImprove,
    })
      .then((response: AxiosResponse) => {
        const nextState = response.data.personalization_state;

        setPersonalizationState(nextState);
        onStateChange(nextState);
        increasePersonalizationProgress();
        startPolling();
      })
      .catch((error) => {
        setPersonalizationState(enrolment.personalization_state);
        onStateChange(enrolment.personalization_state);
        clearTimers();
        resetPollingStartedAt();
        errorNotification(
          "Error when activating personalisation",
          JSON.stringify(error?.response?.data),
        );
      })
      .finally(() => {
        setIsEnableBusy(false);
      });
  };

  const handleFieldChange = (
    field:
      | "personalizationName"
      | "personalizationCountry"
      | "personalizationWorkingOn"
      | "personalizationMustImprove",
    value: string,
  ) => {
    const setters = {
      personalizationName: setPersonalizationName,
      personalizationCountry: setPersonalizationCountry,
      personalizationWorkingOn: setPersonalizationWorkingOn,
      personalizationMustImprove: setPersonalizationMustImprove,
    };

    setters[field](value);
    setErrors((currentErrors) => ({ ...currentErrors, [field]: false }));
  };

  useEffect(() => {
    setPersonalizationState(
      personalizationStateProp || enrolment.personalization_state,
    );
  }, [enrolment.personalization_state, personalizationStateProp]);

  useEffect(() => {
    if (!isModalOpen) return;

    setPersonalizationProgress(0);
    setRandomMessage(personalizationMessages[0]);
    getPersonalizationConfig();
  }, [isModalOpen]);

  useEffect(
    () => () => {
      clearTimers();
      resetPollingStartedAt();
    },
    [],
  );

  const isGenerating = isGeneratingPersonalization(personalizationState);

  return (
    <Modal
      size="small"
      open={isModalOpen}
      className={`personalization-modal pz-modal ${isGenerating ? "pz-load" : ""}`}
      closeIcon={false}
      closeOnDimmerClick={!isGenerating}
      closeOnEscape={!isGenerating}
      onClose={closeModal}
    >
      <Modal.Content>
        {!isGenerating && (
          <button
            className="pz-close"
            type="button"
            onClick={closeModal}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6 L18 18 M18 6 L6 18" strokeLinecap="round" />
            </svg>
          </button>
        )}
        {!isGenerating && (
          <PersonalizationModalContent
            title="Re-personalise this course"
            buttonLabel="Re-personalise my course"
            loading={loading}
            isButtonDisabled={isEnableBusy || loading}
            buttonClassName={`btn btn-blue ${isEnableBusy ? "loading" : ""}`}
            personalizationName={personalizationName}
            personalizationCountry={personalizationCountry}
            personalizationWorkingOn={personalizationWorkingOn}
            personalizationMustImprove={personalizationMustImprove}
            errors={errors}
            onSubmit={activatePersonalization}
            onFieldChange={handleFieldChange}
          />
        )}
        {isGenerating && (
          <PersonalizationGeneratingContent
            progress={personalizationProgress}
            message={randomMessage}
          />
        )}
      </Modal.Content>
    </Modal>
  );
};
